/**
 * Base class for the eleven dashboards. A dashboard owns one CanvasPanel, a filter state, a
 * drill path (overview -> list -> record) and renders only when something changes. Record
 * views expose a RecordRef and a "Raise query" action.
 */
import * as THREE from 'three';
import type { DashboardFilters, DashboardId, RecordRef } from '@clinspace/shared';
import { DASHBOARD_TITLES } from '@clinspace/shared';
import { CanvasPanel, PANEL_PX_H, PANEL_PX_W, type Region } from './CanvasPanel.ts';
import {
  PALETTE,
  chip,
  fillRoundRect,
  font,
  kpiRow,
  text,
  truncate,
  wrapText,
  type Kpi,
  type Rect,
  panelBackground,
  uiMode,
} from './charts.ts';
import type { StudyView } from './StudyView.ts';
import type { Pointer } from '../xr/InputManager.ts';
import type { InteractiveSystem } from '../ui/Interactive.ts';

export interface RecordDetail {
  ref: RecordRef;
  title: string;
  /** Label/value pairs; the `field` key makes a row queryable on that field. */
  rows: { label: string; value: string; field?: string; flag?: boolean }[];
  currentValue?: string;
}

export interface DashboardHost {
  readonly view: StudyView;
  readonly interactive: InteractiveSystem;
  readonly filters: DashboardFilters;
  canRaiseQueries: boolean;
  raiseQuery(
    detail: RecordDetail,
    field: string | undefined,
    dashboard: DashboardId,
    origin: THREE.Vector3,
  ): void;
  pin(dashboard: Dashboard): void;
  broadcast(dashboard: Dashboard): void;
  tooltip(lines: string[] | undefined, at: THREE.Vector3 | undefined): void;
  /** Opens the filter editor for a given key. */
  editFilter(dashboard: Dashboard, key: keyof DashboardFilters, origin: THREE.Vector3): void;
  /** Annotate mode: selections drop a shared note instead of acting. */
  annotating(): boolean;
  annotate(dashboard: Dashboard, label: string, at: THREE.Vector3): void;
  /** The patient ward, when the room has one (profile panels offer its actions). */
  ward?: WardHost;
}

export type WardAction = 'overlays' | 'timeline' | 'compare' | 'table' | 'go' | 'pull' | 'filter';

export interface WardHost {
  action(dashboard: Dashboard, action: WardAction, subjectId: string, origin: THREE.Vector3): void;
  /** Label for the overlay toggle chip, for example "Overlays: labs". */
  overlayLabel(): string;
  timelineOn(subjectId: string): boolean;
  /** True while this subject's mannequin is pulled out of the formation for review. */
  pulled(subjectId: string): boolean;
  /** True while a profile of this subject is pinned to the table. */
  onTable(subjectId: string): boolean;
  /** True while the room's dashboards are filtered to this subject (the profile's switch). */
  filtered(subjectId: string): boolean;
  /** True for a profile panel standing in the ward beside its mannequin. */
  inWard(dashboard: Dashboard): boolean;
}

export interface ViewRegion {
  kind: 'drill' | 'back' | 'action' | 'filter' | 'record' | 'info';
  /** For drill: pushes this segment onto the path. */
  segment?: string;
  action?: 'pin' | 'broadcast' | 'raise' | 'clearFilters';
  filterKey?: keyof DashboardFilters;
  field?: string;
}

export const PANEL_W = 2.4;
export const PANEL_H = 1.53;

export interface DashboardOptions {
  /**
   * Draw this many times wider at the same height (the centre screen's 1.5, 2026-10-07). Every
   * dashboard lays out from its content rectangle, so its charts and tables take the width.
   */
  wide?: number;
}

export abstract class Dashboard {
  abstract readonly id: DashboardId;
  readonly panel: CanvasPanel;
  readonly group = new THREE.Group();
  path: string[] = [];
  filters: DashboardFilters = {};
  private attached = false;
  /** Set by subclasses when a record is in view. */
  protected detail: RecordDetail | undefined;
  /** 3D content hook: subclasses add meshes to `group` and refresh them here. */
  protected update3d(): void {}
  flashUntil = 0;
  /** Set on clones pinned to the table: the shared pin id. */
  pinId: string | undefined;
  /** The KPI tiles drawn last; used by the compare row. */
  lastKpis: Kpi[] = [];
  /** Bumped whenever data, filters or the path change, so the stage layer rebuilds this stage. */
  stageVersion = 0;
  /** Another panel snapped beside this one for comparison. */
  compareTo: Dashboard | undefined;
  /** Where the panel lives when not grabbed (the arc frame or a table slot). */
  readonly home = { position: new THREE.Vector3(), quaternion: new THREE.Quaternion(), scale: 1 };

  /** Width over a standard panel's (1 on the arc, 1.5 on the centre screen). */
  readonly wide: number;

  constructor(
    protected readonly host: DashboardHost,
    options: DashboardOptions = {},
  ) {
    this.wide = options.wide ?? 1;
    this.panel = new CanvasPanel({
      width: PANEL_W * this.wide,
      height: PANEL_H,
      pixelWidth: Math.round(PANEL_PX_W * this.wide),
      pixelHeight: PANEL_PX_H,
      name: `dashboard-${this.constructor.name}`,
      // In the chamber the dashboards are slightly see-through glass with a glowing edge.
      glass: uiMode() === 'chamber',
    });
    this.group.add(this.panel.mesh);
    this.panel.setDraw((ctx, panel) => this.draw(ctx, panel));
    this.panel.onSelect = (region, pointer, point) => this.select(region, pointer, point);
    this.panel.onHoverChange = (region, point) =>
      this.host.tooltip(region?.tooltip, region ? point : undefined);
  }

  get title(): string {
    return DASHBOARD_TITLES[this.id];
  }

  get view(): StudyView {
    return this.host.view;
  }

  attach(): void {
    if (this.attached) return;
    this.attached = true;
    this.panel.attach(this.host.interactive);
  }

  detach(): void {
    if (!this.attached) return;
    this.attached = false;
    this.panel.detach(this.host.interactive);
  }

  /** Data or filter changed: redraw, refresh 3D and rebuild the stage. */
  invalidate(): void {
    this.panel.invalidate();
    this.update3d();
    this.stageVersion++;
  }

  setFilters(filters: DashboardFilters): void {
    this.filters = { ...filters };
    this.invalidate();
  }

  setPath(path: string[]): void {
    this.path = [...path];
    // The record view is recomputed from the path on the next draw.
    this.detail = undefined;
    this.invalidate();
  }

  /** What the room shares about this screen: its drill path and its filters. */
  viewState(): { path: string[]; filters: DashboardFilters } {
    return { path: [...this.path], filters: cleanFilters(this.filters) };
  }

  /** Someone else's view of this screen; true when it changed. */
  setView(state: { path?: unknown; filters?: unknown }): boolean {
    const path = Array.isArray(state.path) ? state.path.slice(0, 8).map(String) : [];
    const filters =
      typeof state.filters === 'object' && state.filters
        ? cleanFilters(state.filters as DashboardFilters)
        : {};
    const same =
      JSON.stringify(path) === JSON.stringify(this.path) &&
      JSON.stringify(filters) === JSON.stringify(cleanFilters(this.filters));
    if (same) return false;
    this.path = path;
    this.filters = filters;
    this.detail = undefined;
    this.invalidate();
    return true;
  }

  drillTo(segment: string): void {
    this.path.push(segment);
    this.detail = undefined;
    this.invalidate();
  }

  back(): void {
    this.path.pop();
    this.detail = undefined;
    this.invalidate();
  }

  currentDetail(): RecordDetail | undefined {
    return this.detail;
  }

  /** Draws attention for a few seconds (a broadcast landed here). */
  flash(ms = 3000): void {
    this.flashUntil = performance.now() + ms;
    this.panel.invalidate();
  }

  /** Called once per frame by the room scene. */
  update(): void {
    if (this.flashUntil && performance.now() >= this.flashUntil) {
      this.flashUntil = 0;
      this.panel.invalidate();
    }
    this.panel.update();
  }

  /** Back to the frame, uncompared. */
  tidy(): void {
    this.compareTo = undefined;
    this.goHome();
    this.panel.invalidate();
  }

  /** Puts the panel back at its home transform. */
  goHome(): void {
    this.group.position.copy(this.home.position);
    this.group.quaternion.copy(this.home.quaternion);
    this.group.scale.setScalar(this.home.scale);
  }

  /** Dev hooks: the regions drawn last frame, and selecting one by id without a pointer. */
  regionIds(): { id: string; kind: string }[] {
    return this.panel
      .allRegions()
      .map((r) => ({ id: r.id, kind: (r.data as ViewRegion | undefined)?.kind ?? 'none' }));
  }

  selectRegion(id: string): boolean {
    const region = this.panel.allRegions().find((r) => r.id === id);
    if (!region) return false;
    const centre = this.panel.toWorld(region.x + region.w / 2, region.y + region.h / 2);
    this.select(region, undefined, centre);
    return true;
  }

  dispose(): void {
    this.detach();
    this.panel.dispose();
  }

  /* ---------- Frame: header, filter bar, content, footer ---------- */

  private draw(ctx: CanvasRenderingContext2D, panel: CanvasPanel): void {
    const W = panel.pxW;
    const H = panel.pxH;
    panelBackground(ctx, W, H, 14);
    // Content first: subclasses decide here whether a record is in view, and the header
    // actions depend on that.
    const content: Rect = { x: 16, y: 78, w: W - 32, h: H - 94 };
    // Always let the dashboard draw from its current path: it sets the record view itself, and
    // some record views draw more than the record table (a subject with its visit list).
    this.drawContent(ctx, panel, content);
    // Header: title, breadcrumb, actions.
    text(ctx, this.title, 16, 26, { size: 17, weight: 600, display: true });
    const crumbs = this.path.length
      ? `  ›  ${this.path.map((p) => this.segmentLabel(p)).join('  ›  ')}`
      : '';
    if (crumbs) {
      ctx.font = font(17, 600, true);
      const tw = ctx.measureText(this.title).width;
      text(ctx, crumbs, 16 + tw, 26, { size: 12, colour: PALETTE.muted, maxWidth: W - tw - 300 });
    }
    let ax = W - 16;
    const action = (label: string, key: ViewRegion['action'], active = false, enabled = true) => {
      ctx.font = font(11, 600);
      const w = ctx.measureText(label).width + 20;
      ax -= w;
      const id = `action:${key}`;
      const r = chip(ctx, label, ax, 8, { active, hovered: panel.isHovered(id), h: 22 });
      if (!enabled) {
        ctx.fillStyle = PALETTE.surfaceSoft;
        roundRectFill(ctx, r);
      }
      panel.region<ViewRegion>({
        id,
        ...r,
        data: { kind: 'action', action: key },
        tooltip: enabled ? undefined : ['Sponsor Observers cannot raise queries'],
      });
      ax -= 6;
    };
    if (this.detail)
      action(
        this.host.canRaiseQueries ? 'Raise query' : 'Raise query (view only)',
        'raise',
        true,
        this.host.canRaiseQueries,
      );
    action('Broadcast', 'broadcast');
    action(this.pinId ? 'Unpin' : 'Pin to table', 'pin');
    if (this.path.length) {
      const r = chip(ctx, '‹ Back', ax - 60, 8, { hovered: panel.isHovered('back'), h: 22 });
      panel.region<ViewRegion>({ id: 'back', ...r, data: { kind: 'back' } });
    }
    // Filter bar.
    this.drawFilterBar(ctx, panel, { x: 16, y: 42, w: W - 32, h: 26 });
    if (this.compareTo) {
      text(
        ctx,
        `Comparing with ${this.compareTo.title}${this.compareTo.pinId ? ' (pinned clone)' : ''}`,
        W - 16,
        H - 8,
        { size: 10, colour: PALETTE.teal, align: 'right' },
      );
    }
    // Flash border when broadcast.
    if (performance.now() < this.flashUntil) {
      ctx.strokeStyle = PALETTE.teal;
      ctx.lineWidth = 6;
      roundRectStroke(ctx, { x: 3, y: 3, w: W - 6, h: H - 6 });
    }
  }

  private drawFilterBar(ctx: CanvasRenderingContext2D, panel: CanvasPanel, area: Rect): void {
    let x = area.x;
    const f = this.filters;
    const items: { key: keyof DashboardFilters; label: string; value: string | undefined }[] = [
      { key: 'siteId', label: 'Site', value: f.siteId ? this.view.siteLabel(f.siteId) : undefined },
      { key: 'arm', label: 'Arm', value: f.arm },
      {
        key: 'visitFrom',
        label: 'Visits',
        value: f.visitFrom || f.visitTo ? `${f.visitFrom ?? '…'} – ${f.visitTo ?? '…'}` : undefined,
      },
      {
        key: 'dateFrom',
        label: 'Dates',
        value: f.dateFrom || f.dateTo ? `${f.dateFrom ?? '…'} – ${f.dateTo ?? '…'}` : undefined,
      },
    ];
    // Patient filters from the ward console only show while set.
    if (f.subjectId) items.push({ key: 'subjectId', label: 'Subject', value: f.subjectId });
    if (f.subjectIds?.length)
      items.push({ key: 'subjectIds', label: 'Review', value: f.subjectIds.join(', ') });
    if (f.sex) items.push({ key: 'sex', label: 'Sex', value: f.sex === 'F' ? 'Female' : 'Male' });
    if (f.status) items.push({ key: 'status', label: 'Status', value: f.status });
    if (f.flag) items.push({ key: 'flag', label: 'Flag', value: f.flag });
    if (f.ageBand) items.push({ key: 'ageBand', label: 'Age', value: f.ageBand });
    if (f.bmiBucket) items.push({ key: 'bmiBucket', label: 'BMI', value: f.bmiBucket });
    for (const item of items) {
      const id = `filter:${item.key}`;
      const r = chip(
        ctx,
        item.value ? `${item.label}: ${item.value}` : `${item.label}: all`,
        x,
        area.y,
        { active: !!item.value, hovered: panel.isHovered(id), h: 22, size: 10 },
      );
      panel.region<ViewRegion>({ id, ...r, data: { kind: 'filter', filterKey: item.key } });
      x += r.w + 6;
    }
    if (Object.keys(f).some((k) => f[k as keyof DashboardFilters])) {
      const r = chip(ctx, 'Clear', x, area.y, {
        hovered: panel.isHovered('action:clearFilters'),
        h: 22,
        size: 10,
      });
      panel.region<ViewRegion>({
        id: 'action:clearFilters',
        ...r,
        data: { kind: 'action', action: 'clearFilters' },
      });
    }
  }

  /** Default record detail renderer: a two-column table with queryable rows. */
  protected drawDetail(
    ctx: CanvasRenderingContext2D,
    panel: CanvasPanel,
    area: Rect,
    detail: RecordDetail,
  ): void {
    text(ctx, detail.title, area.x, area.y + 18, { size: 15, weight: 700 });
    const rowH = 24;
    const LONG = 48;
    const indexed = detail.rows.map((row, i) => ({ row, i }));
    const short = indexed.filter(({ row }) => row.value.length <= LONG);
    const long = indexed.filter(({ row }) => row.value.length > LONG);
    const cols = short.length > 12 ? 2 : 1;
    const colW = area.w / cols;
    const perCol = Math.ceil(short.length / cols);
    const registerRow = (r: Rect, row: RecordDetail['rows'][number], i: number) => {
      if (!row.field) return;
      panel.region<ViewRegion>({
        id: `record:${i}`,
        ...r,
        data: { kind: 'record', field: row.field },
        tooltip: this.host.canRaiseQueries ? [`Raise a query on "${row.field}"`] : ['View only'],
      });
    };
    short.forEach(({ row, i }, n) => {
      const col = Math.floor(n / perCol);
      const r = {
        x: area.x + col * colW,
        y: area.y + 30 + (n % perCol) * rowH,
        w: colW - 10,
        h: rowH,
      };
      if (panel.isHovered(`record:${i}`)) fillRoundRect(ctx, r, 4, PALETTE.hover);
      if (row.flag) {
        ctx.fillStyle = PALETTE.crimson;
        ctx.fillRect(r.x, r.y + 5, 3, rowH - 10);
      }
      text(ctx, row.label, r.x + 8, r.y + 16, { size: 11, colour: PALETTE.muted });
      const vx = r.x + Math.min(170, colW * 0.42);
      ctx.font = font(12, 500);
      text(ctx, truncate(ctx, row.value, r.w - (vx - r.x) - 6), vx, r.y + 16, { size: 12 });
      registerRow(r, row, i);
    });
    // Long values (narratives, descriptions) wrap across the full width below the table.
    let y = area.y + 30 + perCol * rowH + 10;
    for (const { row, i } of long) {
      ctx.font = font(12, 500);
      const lines = wrapText(ctx, row.value, area.w - 24);
      const h = 18 + lines.length * 17;
      if (y + h > area.y + area.h) break;
      const r = { x: area.x, y, w: area.w - 10, h };
      if (panel.isHovered(`record:${i}`)) fillRoundRect(ctx, r, 4, PALETTE.hover);
      if (row.flag) {
        ctx.fillStyle = PALETTE.crimson;
        ctx.fillRect(r.x, r.y + 5, 3, h - 10);
      }
      text(ctx, row.label, r.x + 8, r.y + 14, { size: 11, colour: PALETTE.muted });
      lines.forEach((l, k) => text(ctx, l, r.x + 8, r.y + 31 + k * 17, { size: 12 }));
      registerRow(r, row, i);
      y += h + 6;
    }
  }

  /** KPI tiles with a tooltip region per tile (label, value, sub). */
  protected kpis(
    panel: CanvasPanel,
    ctx: CanvasRenderingContext2D,
    kpis: Kpi[],
    area: Rect,
  ): Rect[] {
    const rects = kpiRow(ctx, kpis, area);
    this.lastKpis = kpis;
    const other = this.compareTo && this.compareTo.id === this.id ? this.compareTo.lastKpis : [];
    rects.forEach((r, i) => {
      const k = kpis[i]!;
      const o = other[i];
      if (o && o.label === k.label) {
        const a = numberOf(k.value);
        const b = numberOf(o.value);
        const delta = a !== undefined && b !== undefined ? a - b : undefined;
        const colour =
          delta === undefined || delta === 0
            ? PALETTE.muted
            : delta > 0
              ? PALETTE.amber
              : PALETTE.teal;
        text(
          ctx,
          `vs ${o.value}${delta !== undefined && delta !== 0 ? ` (${delta > 0 ? '+' : ''}${Math.round(delta * 10) / 10})` : ''}`,
          r.x + r.w - 8,
          r.y + 20,
          { size: 10, colour, align: 'right' },
        );
      }
      panel.region<ViewRegion>({
        id: `kpi:${i}`,
        ...r,
        data: { kind: 'info' },
        tooltip: [k.label, k.sub ? `${k.value} · ${k.sub}` : k.value],
        selectable: false,
      });
    });
    return rects;
  }

  protected abstract drawContent(
    ctx: CanvasRenderingContext2D,
    panel: CanvasPanel,
    area: Rect,
  ): void;

  /** Informational regions (paging controls and the like); subclasses override. */
  protected onInfo(_regionId: string, _point?: THREE.Vector3): void {}

  /** Human label for a path segment (subclasses override for ids). */
  protected segmentLabel(segment: string): string {
    return segment;
  }

  private select(region: Region, pointer: Pointer | undefined, point: THREE.Vector3): void {
    const data = region.data as ViewRegion | undefined;
    if (!data) return;
    if (this.host.annotating()) {
      this.host.annotate(
        this,
        region.tooltip?.[0] ?? this.segmentLabel(data.segment ?? region.id),
        point,
      );
      return;
    }
    switch (data.kind) {
      case 'back':
        this.back();
        break;
      case 'drill':
        if (data.segment) this.drillTo(data.segment);
        break;
      case 'action':
        if (data.action === 'pin') this.host.pin(this);
        else if (data.action === 'broadcast') this.host.broadcast(this);
        else if (data.action === 'clearFilters') this.setFilters({});
        else if (data.action === 'raise' && this.detail && this.host.canRaiseQueries)
          this.host.raiseQuery(this.detail, undefined, this.id, point);
        break;
      case 'filter':
        if (data.filterKey) this.host.editFilter(this, data.filterKey, point);
        break;
      case 'record':
        if (this.detail && this.host.canRaiseQueries)
          this.host.raiseQuery(this.detail, data.field, this.id, point);
        break;
      case 'info':
        this.onInfo(region.id, point);
        break;
    }
    void pointer;
  }
}

/** Filters without empty keys, in a fixed key order (so equal filters compare equal). */
export function cleanFilters(f: DashboardFilters): DashboardFilters {
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(f).sort()) {
    const v = (f as Record<string, unknown>)[k];
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v) && v.length === 0) continue;
    out[k] = v;
  }
  return out as DashboardFilters;
}

function numberOf(value: string): number | undefined {
  const m = /-?\d+(?:\.\d+)?/.exec(value.replace(/,/g, ''));
  return m ? Number(m[0]) : undefined;
}

function roundRectFill(ctx: CanvasRenderingContext2D, r: Rect): void {
  fillRoundRect(ctx, r, r.h / 2, ctx.fillStyle as string);
}

function roundRectStroke(ctx: CanvasRenderingContext2D, r: Rect): void {
  ctx.beginPath();
  ctx.roundRect(r.x, r.y, r.w, r.h, 12);
  ctx.stroke();
}
